import { BlockchainNetwork, NGN, RE_USD, VND } from "@renec-foundation/relend-adapter"
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, Token } from "@solana/spl-token"
import { Connection, PublicKey, SYSVAR_RENT_PUBKEY, SystemProgram, TransactionInstruction } from "@solana/web3.js"
import BigNumber from "bignumber.js"
import { getConnection } from "./config/connection"
import {
  APS,
  ERRORS,
  GAST,
  PI,
  PROP,
  REL,
  RELEND,
  RENEC,
  RE_BTC,
  RE_ETH,
  RE_NGN,
  RE_VND,
  TOKEN_CDN_URL,
  VE_REL,
} from "./constants"
import Client from "./custom-utl-sdk"
import { UTLCdnTokenList, Token as UtlToken } from "./custom-utl-sdk/types"

export type StyleValue = string | number | StyleObject

export interface StyleObject {
  [key: string]: StyleValue | StyleValueObject | undefined
}

interface StyleValueObject {
  [property: string]: StyleValue | StyleValueObject | undefined
}

export const isEmpty = (value: any): boolean => {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object") return Object.keys(value).length === 0
  return false
}

export const randomInteger = (fromNum: number, toNum: number) => {
  return Math.floor(Math.random() * (toNum - fromNum + 1)) + fromNum
}

export const combineStyles = (...styles: StyleObject[]) => styles.reduce((acc, val) => Object.assign(acc, val), {})

export const replaceLocaleStringWithValues = (localeString: string, ...values: string[]) => {
  return localeString.replace(/%s/g, (match) => {
    const replacement = values.shift()
    return typeof replacement !== "undefined" ? replacement : match
  })
}

export const calculateTotalUSD = (base: BigNumber, amount: BigNumber, price: BigNumber) =>
  base.plus(amount.multipliedBy(price))

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const errorList = [
  "Failed to unpack instruction data",
  "Account is already initialized",
  "Lamport balance below rent-exempt threshold",
  "Market authority is invalid",
  "Market owner is invalid",
  "Input account owner is not the program plusress",
  "Input token account is not owned by the correct token program id",
  "Input token account is not valid",
  "Input token mint account is not valid",
  "Input token program account is not valid",
  "Input amount is invalid",
  "Input config value is invalid",
  "Input account must be a signer",
  "Invalid account input",
  "Math operation overflow",
  "Token initialize mint failed",
  "Token initialize account failed",
  "Token transfer failed",
  "Token mint to failed",
  "Token burn failed",
  "Insufficient liquidity available",
  "Input reserve has collateral disabled",
  "Reserve state needs to be refreshed",
  "Withdraw amount too small",
  "Withdraw amount too large",
  "Borrow amount too small to receive liquidity after fees",
  "Borrow amount too large for deposited collateral",
  "Repay amount too small to transfer liquidity",
  "Liquidation amount too small to receive collateral",
  "Cannot liquidate healthy obligations",
  "Obligation state needs to be refreshed",
  "Obligation reserve limit exceeded",
  "Obligation owner is invalid",
  "Obligation deposits are empty",
  "Obligation borrows are empty",
  "Obligation deposits have zero value",
  "Obligation borrows have zero value",
  "Invalid obligation collateral",
  "Invalid obligation liquidity",
  "Obligation collateral is empty",
  "Obligation liquidity is empty",
  "Interest rate is negative",
  "Input oracle config is invalid",
  "Input flash loan receiver program account is not valid",
  "Not enough liquidity after flash loan",
  "Null oracle config",
]

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const errorObjectToDebug = {
  "0x0": "Failed to unpack instruction data",
  "0x1": "Account is already initialized",
  "0x2": "Lamport balance below rent-exempt threshold",
  "0x3": "Market authority is invalid",
  "0x4": "Market owner is invalid",
  "0x5": "Input account owner is not the program plusress",
  "0x6": "Input token account is not owned by the correct token program id",
  "0x7": "Input token account is not valid",
  "0x8": "Input token mint account is not valid",
  "0x9": "Input token program account is not valid",
  "0xa": "Input amount is invalid",
  "0xb": "Input config value is invalid",
  "0xc": "Input account must be a signer",
  "0xd": "Invalid account input",
  "0xe": "Math operation overflow",
  "0xf": "Token initialize mint failed",
  "0x10": "Token initialize account failed",
  "0x11": "Token transfer failed",
  "0x12": "Token mint to failed",
  "0x13": "Token burn failed",
  "0x14": "Insufficient liquidity available",
  "0x15": "Input reserve has collateral disabled",
  "0x16": "Reserve state needs to be refreshed",
  "0x17": "Withdraw amount too small",
  "0x18": "Withdraw amount too large",
  "0x19": "Borrow amount too small to receive liquidity after fees",
  "0x1a": "Borrow amount too large for deposited collateral",
  "0x1b": "Repay amount too small to transfer liquidity",
  "0x1c": "Liquidation amount too small to receive collateral",
  "0x1d": "Cannot liquidate healthy obligations",
  "0x1e": "Obligation state needs to be refreshed",
  "0x1f": "Obligation reserve limit exceeded",
  "0x20": "Obligation owner is invalid",
  "0x21": "Obligation deposits are empty",
  "0x22": "Obligation borrows are empty",
  "0x23": "Obligation deposits have zero value",
  "0x24": "Obligation borrows have zero value",
  "0x25": "Invalid obligation collateral",
  "0x26": "Invalid obligation liquidity",
  "0x27": "Obligation collateral is empty",
  "0x28": "Obligation liquidity is empty",
  "0x29": "Interest rate is negative",
  "0x2a": "Input oracle config is invalid",
  "0x2b": "Input flash loan receiver program account is not valid",
  "0x2c": "Not enough liquidity after flash loan",
  "0x2d": "Null oracle config",
  "0x3d": "Oracle price is too old",
}

export type TokenSymbol =
  | "reVND"
  | "reNGN"
  | "VND"
  | "NGN"
  | "RENEC"
  | "reUSD"
  | "reETH"
  | "reBTC"
  | "GAST"
  | "REL"
  | "RELEND"
  | "PROP"
  | "Pi"
  | "APS"
  | "veREL"

type TokenDecimalDigitsType = {
  [key in TokenSymbol]: number
}

export const tokenDecimalDigits: TokenDecimalDigitsType = {
  [RE_VND]: 0,
  [VND]: 0,
  [RE_NGN]: 0,
  [NGN]: 0,
  [RENEC]: 3,
  [RE_USD]: 3,
  [RE_ETH]: 6,
  [RE_BTC]: 9,
  [GAST]: 3,
  [REL]: 3,
  [RELEND]: 6, // this is for testnet
  [VE_REL]: 6, // this is for testnet
  [PROP]: 9,
  [PI]: 0,
  [APS]: 9,
}

export const isStringEmpty = (str?: any) => {
  return str === undefined || (typeof str === "string" && str.trim() === "")
}

const ERROR_VALUE = BigNumber(0.000000001)

export const roundBigDecimalNumber = (amount: BigNumber, decimals = 9) => {
  return amount.isLessThan(amount.decimalPlaces(decimals))
    ? amount.decimalPlaces(decimals)
    : amount.plus(ERROR_VALUE).decimalPlaces(decimals)
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const stringifyVariable = (a: any) => {
  if (a === undefined) {
    return "undefined"
  } else if (a === null) {
    return "null"
  } else if (Array.isArray(a)) {
    return JSON.stringify(a)
  } else if (typeof a === "object" && Object.prototype.toString.call(a) === "[object Object]") {
    return JSON.stringify(a)
  } else {
    return a.toString()
  }
}

export const createAssociatedTokenAccountInstruction = (
  associatedTokenAddress: PublicKey,
  payer: PublicKey,
  walletAddress: PublicKey,
  splTokenMintAddress: PublicKey,
) => {
  const keys = [
    {
      pubkey: payer,
      isSigner: true,
      isWritable: true,
    },
    {
      pubkey: associatedTokenAddress,
      isSigner: false,
      isWritable: true,
    },
    {
      pubkey: walletAddress,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: splTokenMintAddress,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: SystemProgram.programId,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: TOKEN_PROGRAM_ID,
      isSigner: false,
      isWritable: false,
    },
    {
      pubkey: SYSVAR_RENT_PUBKEY,
      isSigner: false,
      isWritable: false,
    },
  ]
  return new TransactionInstruction({
    keys,
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    data: Buffer.from([]),
  })
}

export const getOrCreateAssociatedTokenAccountInstruction = async (
  connection: Connection,
  payer: PublicKey,
  walletAddress: PublicKey,
  splTokenMintAddress: PublicKey,
) => {
  const ixs: TransactionInstruction[] = []
  const userAtaAddress = await Token.getAssociatedTokenAddress(
    ASSOCIATED_TOKEN_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
    splTokenMintAddress,
    walletAddress,
  )
  const associatedTokenAccountInfo = await connection.getAccountInfo(userAtaAddress)
  if (associatedTokenAccountInfo === null) {
    // Associated token address doesn't exist, create it
    const toAtaIxs = createAssociatedTokenAccountInstruction(userAtaAddress, payer, walletAddress, splTokenMintAddress)
    ixs.push(toAtaIxs)
  }

  return {
    ixs,
    userAtaAddress,
  }
}

export const sliceString = (str: string, startPadding = 5, endPadding = 5) =>
  str.slice(0, startPadding) + "..." + str.slice(-endPadding)

export const generateAssociatedTokenAccountAddress = (mint: PublicKey, buyer: PublicKey) => {
  return PublicKey.findProgramAddressSync(
    [buyer.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0]
}

export const isStringInStringArray = (str: string, stringArray: string, delimiter = ","): boolean => {
  const list = (stringArray || "").split(delimiter)
  return list.includes(str)
}

export const shortenAddress = (address?: string) => {
  if (!address) return ""
  return `${address.slice(0, 4) + "..." + address.slice(-4)}`
}

export const capitalizeFirstLetter = (str: string) => {
  const lowercased = str.toLowerCase()
  return lowercased.charAt(0).toUpperCase() + lowercased.slice(1)
}

export const errorWrapper = async (fn: Function) => {
  try {
    await fn()
  } catch (err) {
    console.error(err)
  }
}

export const getCDNTokenList = async (): Promise<UTLCdnTokenList | null> => {
  const utl = new Client()
  return await utl.downloadTokenlist(TOKEN_CDN_URL)
}

export const getSpecificTokenInfo = async (mintAddress: string): Promise<UtlToken | null> => {
  const utl = new Client()
  const tokens: UtlToken[] = await utl.fetchMints(TOKEN_CDN_URL, [new PublicKey(mintAddress || "")])
  return tokens[0] ?? ({} as Token)
}

/**
 * Format error response from transaction to a common pattern response
 */
export const commonErrorResponse = (error: any, trans: any) => {
  let errorMessage: string = extractErrorMessage(error, trans)

  errorMessage = handleFailedMessage(errorMessage)
  errorMessage = handleInsufficientRenec(error, errorMessage)
  errorMessage = handleGaslessErrors(errorMessage, trans)

  console.error(error)
  return {
    isSucceeded: false,
    error: errorMessage,
  }
}

export const formatErrorMsg = (errorMessage: string, trans: any) => {
  if (errorMessage.includes("postSolution error") || errorMessage.includes("Program failed to complete")) {
    return `${trans.errors.system_error}`
  }

  const errorMatch = errorMessage.match(/0x([0-9a-fA-F]+)/)
  const error = errorMatch ? errorMatch[1] : null
  if (!error) {
    return errorMessage
  }
  // return `${errorList[parseInt(error, 16)]}`
  return `${trans.errors.system_error} (0x${error})`
}

/**
 * Extract error message from error object or string
 */
const extractErrorMessage = (error: any, trans: any): string => {
  if (typeof error === "string") {
    return error.includes("0x") ? formatErrorMsg(error, trans) : error
  }
  return formatErrorMsg(error.message, trans)
}

/**
 * Extract the specific message after "failed:"
 */
const handleFailedMessage = (errorMessage: string): string => {
  const failedKeyword = "failed:"
  if (!errorMessage || typeof errorMessage !== "string") {
    return errorMessage
  }
  const failedIndex = errorMessage.indexOf(failedKeyword)
  if (failedIndex !== -1) {
    // Extract the specific failure message
    let detailedMessage = errorMessage.substring(failedIndex + failedKeyword.length).trim()

    // Additional parsing to handle special cases if needed
    const programFailureIndex = detailedMessage.indexOf("Program")
    if (programFailureIndex !== -1) {
      // Extract and clean up the message
      detailedMessage = detailedMessage.substring(programFailureIndex).trim()
    }

    // Remove any trailing bracket and any subsequent characters (like the closing quote)
    if (detailedMessage.endsWith('"]')) {
      detailedMessage = detailedMessage.slice(0, -2).trim()
    }

    return detailedMessage
  }

  // Return the original message if the keyword "failed:" is not found
  return errorMessage
  return errorMessage
}

/**
 * Handle insufficient RENEC errors for both desktop/extension and mobile
 */
const handleInsufficientRenec = (error: any, errorMessage: string): string => {
  if (error.logs && error.logs.length > 0) {
    // Desktop / extension
    for (const log of error.logs) {
      if (log.includes(ERRORS.INSUFFIENT_LAMPORTS)) {
        return ERRORS.INSUFFIENT_BALANCE
      }
    }
  } else if (errorMessage.endsWith("0x1") || (typeof error === "string" && error.includes("0x1"))) {
    // Mobile
    return ERRORS.INSUFFIENT_BALANCE
  }
  return errorMessage
}

/**
 * Handle gasless errors
 */
const handleGaslessErrors = (errorMessage: string, trans: any): string => {
  if (errorMessage.includes(ERRORS.TRANSACTION_TOO_LARGE)) {
    return errorMessage.includes(ERRORS.BUILDING_GASLESS_TRANSACTION)
      ? trans.dashboard.modal.errors.gasless_updating
      : trans.dashboard.modal.errors.tx_too_large
  }

  if (errorMessage.includes(ERRORS.MAX_POSITIONS_REACHED)) {
    return trans.dashboard.modal.errors.reduce_positions
  }

  if (errorMessage.includes(ERRORS.BUILDING_GASLESS_TRANSACTION)) {
    const matchResult = errorMessage.match(/{.*}/)
    if (matchResult) {
      const jsonString = matchResult[0]
      const errorObject = JSON.parse(jsonString)
      const formattedErrorMessage = formatErrorMsg(errorObject.message, trans)
      return handleInsufficientRenec(formattedErrorMessage, trans)
    }
  }

  return errorMessage
}

export const isValidNumber = (value?: number) => value && isFinite(value) && !isNaN(value)

export const checkTransactionStatus = async (network: BlockchainNetwork, txHash: string) => {
  try {
    const connection = getConnection(network)
    const txInfo = await connection.getParsedTransaction(txHash, {
      commitment: "confirmed",
    })
    if (txInfo) {
      console.log("Transaction confirmed:", txInfo.transaction.signatures[0])
      return true
    } else {
      console.log("Transaction not found")
      return false
    }
  } catch (error) {
    console.error("Error fetching transaction status:", error)
    return false
  }
}

export const waitForTransactionConfirmation = async (network: BlockchainNetwork, txHash: string, timeout = 3000) => {
  const start = Date.now()

  while (Date.now() - start < timeout) {
    const isConfirmed = await checkTransactionStatus(network, txHash)
    if (isConfirmed) {
      return true
    }
    // Wait for a short period before checking again to avoid spamming the network
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  console.error("Transaction confirmation timeout")
}
